<template>
  <div class="contact-page">
    <!-- Hero -->
    <section class="contact-hero">
      <div class="container hero-inner">
        <p class="eyebrow">GET IN TOUCH</p>
        <h1>Contact Us</h1>
        <p class="hero-desc">
          Reach the right team for partnerships, media buying, compliance, or
          technical support. We respond during Hong Kong business hours.
        </p>
        <div class="hero-meta">
          <a class="meta-chip" href="tel:+85251719054">
            <i class="el-icon-phone"></i>
            +852-51719054
          </a>
          <a
            class="meta-chip meta-chip--outline"
            href="https://www.globalailink.org"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="el-icon-link"></i>
            www.globalailink.org
          </a>
        </div>
      </div>
    </section>

    <!-- Email channels -->
    <section class="channels-section">
      <div class="container">
        <div class="section-head">
          <h2>Business Contacts</h2>
          <p>Select the channel that best matches your inquiry.</p>
        </div>

        <div class="channels-grid">
          <a
            v-for="channel in contactChannels"
            :key="channel.label"
            class="channel-card"
            :href="'mailto:' + channel.email"
          >
            <div class="channel-top">
              <div class="channel-icon">
                <i :class="channel.icon"></i>
              </div>
              <span class="channel-tag">{{ channel.tag }}</span>
            </div>
            <h3>{{ channel.label }}</h3>
            <p class="channel-email">{{ channel.email }}</p>
            <p class="channel-hint">{{ channel.hint }}</p>
            <span class="channel-action">
              Send email
              <i class="el-icon-right"></i>
            </span>
          </a>
        </div>
      </div>
    </section>

    <!-- Office info -->
    <section class="office-section">
      <div class="container">
        <div class="section-head">
          <h2>Office Information</h2>
          <p>Our registered office and general contact details.</p>
        </div>

        <div class="office-grid">
          <article
            v-for="item in officeInfo"
            :key="item.title"
            class="office-card"
          >
            <div class="office-icon">
              <i :class="item.icon"></i>
            </div>
            <h3>{{ item.title }}</h3>
            <p v-if="!item.links">{{ item.content }}</p>
            <template v-else>
              <p v-for="link in item.links" :key="link.text">
                <a v-if="link.href" :href="link.href">{{ link.text }}</a>
                <template v-else>{{ link.text }}</template>
              </p>
            </template>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "ContactUsDetailPage",
  data() {
    return {
      contactChannels: [
        {
          label: "Business Cooperation",
          email: "bd@globalailink.org",
          icon: "el-icon-s-cooperation",
          tag: "Business",
          hint: "General partnerships and cooperation inquiries",
        },

        {
          label: "Search Arbitrage Business",
          email: "bd-searcharbitrage@globalailink.org",
          icon: "el-icon-search",
          tag: "Search",
          hint: "RSOC/AFS and search arbitrage cooperation",
        },
        {
          label: "Lead Generation (CPL)",
          email: "bd-cpl@globalailink.org",
          icon: "el-icon-s-data",
          tag: "LeadGen",
          hint: "CPL campaigns and lead generation partnerships",
        },

        {
          label: "Technical Support",
          email: "support@globalailink.org",
          icon: "el-icon-service",
          tag: "Support",
          hint: "Technical issues and platform support requests",
        },
      ],
      officeInfo: [
        {
          title: "Office Address",
          icon: "el-icon-location-outline",
          content:
            "RM 101A, 1/F., GENPLAS IND BUILDING, 56 HOI YUEN ROAD, KWUN TONG, HONG KONG",
        },
        {
          title: "Business Hours",
          icon: "el-icon-time",
          content: "Monday to Friday, 09:00 – 19:00 (GMT+8, Hong Kong Time)",
        },
        {
          title: "Legal Entity",
          icon: "el-icon-office-building",
          content: "GLOBAL AI LINK LIMITED",
        },
        {
          title: "Technical Support",
          icon: "el-icon-message",
          links: [
            {
              text: "support@globalailink.org",
              href: "mailto:support@globalailink.org",
            },
            { text: "Tel: +852-51719054", href: "tel:+85251719054" },
          ],
        },
      ],
    };
  },
};
</script>

<style scoped>
.contact-page {
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  color: #1c2b4e;
  background-color: #f0f7ff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero */
.contact-hero {
  position: relative;
  padding: 160px 0 96px;
  background-image: url("../../static/images/publisher/hero-bg.jpg");
  background-size: cover;
  background-position: center top;
  background-attachment: fixed;
  background-repeat: no-repeat;
  overflow: hidden;
}

.contact-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(240, 247, 255, 0.45) 0%,
    rgba(255, 255, 255, 0.95) 100%
  );
  pointer-events: none;
}

.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(75, 137, 255, 0.1);
  padding: 48px 40px;
  border-radius: 24px;
  box-shadow: 0 16px 48px rgba(28, 43, 78, 0.05);
  backdrop-filter: blur(8px);
}

.eyebrow {
  margin: 0 0 16px;
  font-size: 13px;
  letter-spacing: 1.8px;
  color: #4b89ff;
  font-weight: 700;
}

.hero-inner h1 {
  margin: 0 0 12px;
  font-size: 52px;
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.hero-inner h1::after {
  content: "";
  display: block;
  width: 64px;
  height: 4px;
  margin: 20px auto 24px;
  background: linear-gradient(90deg, #4b89ff, #296dff);
  border-radius: 2px;
}

.hero-desc {
  margin: 0 auto 36px;
  font-size: 18px;
  line-height: 1.85;
  color: #475569;
  max-width: 600px;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 999px;
  background: linear-gradient(90deg, #4b89ff, #296dff);
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.meta-chip--outline {
  background: #ffffff;
  color: #296dff;
  border: 1px solid rgba(75, 137, 255, 0.35);
  box-shadow: 0 4px 14px rgba(28, 43, 78, 0.06);
}

.meta-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(75, 137, 255, 0.28);
}

/* Section head */
.section-head {
  text-align: center;
  max-width: 620px;
  margin: 0 auto 44px;
}

.section-head h2 {
  margin: 0 0 8px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.2;
}

.section-head h2::after {
  content: "";
  display: block;
  width: 64px;
  height: 3px;
  margin: 16px auto 0;
  background: linear-gradient(90deg, #4b89ff, #296dff);
  border-radius: 2px;
}

.section-head p {
  margin: 18px 0 0;
  font-size: 16px;
  line-height: 1.75;
  color: #64748b;
}

/* Channels */
.channels-section {
  padding: 72px 0 80px;
  background: #ffffff;
}

.channels-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.channel-card {
  display: flex;
  flex-direction: column;
  padding: 32px 28px;
  background: #ffffff;
  border: 1px solid rgba(75, 137, 255, 0.08);
  border-radius: 20px;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 10px 32px rgba(28, 43, 78, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.channel-card:hover {
  transform: translateY(-6px);
  border-color: rgba(75, 137, 255, 0.2);
  box-shadow: 0 18px 48px rgba(75, 137, 255, 0.1);
}

.channel-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.channel-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(75, 137, 255, 0.12) 0%, rgba(75, 137, 255, 0.02) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.channel-icon i {
  font-size: 24px;
  color: #4b89ff;
}

.channel-tag {
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #4b89ff;
  background: rgba(75, 137, 255, 0.1);
  border-radius: 999px;
}

.channel-card h3 {
  margin: 0 0 8px;
  font-size: 19px;
  font-weight: 700;
  color: #1c2b4e;
}

.channel-email {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #4b89ff;
  word-break: break-all;
}

.channel-hint {
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.7;
  color: #64748b;
  flex: 1;
}

.channel-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1c2b4e;
  transition: color 0.25s ease, gap 0.25s ease;
}

.channel-card:hover .channel-action {
  color: #4b89ff;
  gap: 12px;
}

/* Office */
.office-section {
  padding: 72px 0 96px;
  background: linear-gradient(180deg, #f0f8ff 0%, #ffffff 100%);
}

.office-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}

.office-card {
  padding: 32px 24px;
  text-align: center;
  background: #ffffff;
  border: 1px solid rgba(75, 137, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(28, 43, 78, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.office-card:hover {
  transform: translateY(-6px);
  border-color: rgba(75, 137, 255, 0.2);
  box-shadow: 0 16px 40px rgba(75, 137, 255, 0.1);
}

.office-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(75, 137, 255, 0.12) 0%, rgba(75, 137, 255, 0.02) 100%);
  color: #4b89ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.office-icon i {
  font-size: 24px;
}

.office-card h3 {
  margin: 0 0 12px;
  font-size: 19px;
  font-weight: 700;
  color: #1c2b4e;
}

.office-card p {
  margin: 0 0 6px;
  font-size: 15px;
  line-height: 1.7;
  color: #64748b;
}

.office-card p:last-child {
  margin-bottom: 0;
}

.office-card a {
  color: #4b89ff;
  text-decoration: none;
  font-weight: 600;
  transition: opacity 0.2s;
}

.office-card a:hover {
  opacity: 0.8;
  text-decoration: none;
}

@media (max-width: 1100px) {
  .channels-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .office-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .contact-hero {
    padding-top: 120px;
  }

  .hero-inner h1 {
    font-size: 40px;
  }

  .section-head h2 {
    font-size: 30px;
  }
}

@media (max-width: 576px) {
  .contact-hero {
    padding-bottom: 56px;
  }

  .hero-inner h1 {
    font-size: 32px;
  }

  .hero-desc {
    font-size: 15px;
  }

  .meta-chip {
    width: 100%;
    justify-content: center;
  }

  .channels-section,
  .office-section {
    padding: 56px 0;
  }

  .channels-grid,
  .office-grid {
    grid-template-columns: 1fr;
  }

  .channel-card {
    padding: 20px 18px;
  }
}
</style>
